'use client';

import { useState } from 'react';
import DashboardColumn, { DashboardCardData } from './DashboardColumn';
import StarsBackground from './StarsBackground';

interface FinancesCard {
  id: string;
  title: string;
  type: 'grant' | 'accelerator' | 'vc';
  amount?: string;
  deadline?: string;
  status?: string;
  partner?: string;
  firm?: string;
  taskId: string;
  stage: number; // 0 = Research, 1 = Drafting, 2 = Submitted, 3 = In Review, 4 = Closed
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
}

// Mock data matching the mockup
const mockFinancesData: FinancesCard[] = [
  // Research (stage 0)
  { id: '1', title: 'NSF SBIR Phase I', type: 'grant', amount: '$275k', deadline: '45 days', taskId: '#FIN-52', stage: 0 },
  { id: '2', title: 'Sequoia Capital', type: 'vc', partner: 'Alfred Lin', firm: 'Series A', amount: '$2-5M', taskId: '#FIN-53', stage: 0 },
  { id: '3', title: 'Techstars NYC', type: 'accelerator', amount: '$120k', deadline: '30 days', status: "Winter '26", taskId: '#FIN-54', stage: 0 },
  { id: '4', title: 'DOE Clean Energy', type: 'grant', amount: '$500k', deadline: '60 days', taskId: '#FIN-55', stage: 0 },
  // Drafting (stage 1)
  { id: '5', title: 'NIH STTR Grant', type: 'grant', amount: '$150k', deadline: '21 days', taskId: '#FIN-45', stage: 1 },
  { id: '6', title: 'Y Combinator', type: 'accelerator', amount: '$500k', deadline: '14 days', status: 'S25 Batch', taskId: '#FIN-46', stage: 1 },
  { id: '7', title: 'a16z Seed Fund', type: 'vc', partner: 'Marc Andreessen', firm: 'Seed', amount: '$1-2M', taskId: '#FIN-47', stage: 1 },
  // Submitted (stage 2)
  { id: '8', title: 'AWS Activate', type: 'grant', amount: '$100k', status: 'Credits', taskId: '#FIN-38', stage: 2 },
  { id: '9', title: '500 Global', type: 'accelerator', amount: '$150k', status: 'Batch 32', taskId: '#FIN-39', stage: 2 },
  { id: '10', title: 'State Innovation Grant', type: 'grant', amount: '$50k', deadline: 'Under review', taskId: '#FIN-40', stage: 2 },
  // In Review (stage 3)
  { id: '11', title: 'Google for Startups', type: 'accelerator', amount: '$200k', status: 'Interview scheduled', deadline: '5 days', taskId: '#FIN-32', stage: 3 },
  { id: '12', title: 'First Round Capital', type: 'vc', partner: 'Josh Kopelman', firm: 'Seed', amount: '$1-3M', status: 'Partner meeting', taskId: '#FIN-33', stage: 3 },
  { id: '13', title: 'DARPA SBIR', type: 'grant', amount: '$100k', deadline: '7 days', status: 'Phase 2 review', taskId: '#FIN-34', stage: 3 },
  // Closed (stage 4)
  { id: '14', title: 'Stripe Atlas Credits', type: 'grant', amount: '$5k', status: 'Won ✓', taskId: '#FIN-12', stage: 4 },
  { id: '15', title: 'Microsoft Founders Hub', type: 'grant', amount: '$25k', status: 'Won ✓', taskId: '#FIN-15', stage: 4 },
  { id: '16', title: 'Indie Bio Accelerator', type: 'accelerator', amount: '$45k', status: 'Won ✓', taskId: '#FIN-18', stage: 4 },
  { id: '17', title: 'Angel Syndicate', type: 'vc', partner: 'Various', amount: '$50k', status: 'Closed', taskId: '#FIN-20', stage: 4 },
  { id: '18', title: 'State R&D Credit', type: 'grant', amount: '$12k', status: 'Won ✓', taskId: '#FIN-22', stage: 4 },
];

function FinancesCardView({ card, onCardClick }: { card: FinancesCard; onCardClick?: (taskId: string) => void }) {
  const borderColors = {
    emerald: 'border-l-emerald-500',
    purple: 'border-l-purple-500',
    blue: 'border-l-blue-500',
    amber: 'border-l-amber-500',
  };

  const typeConfig = {
    grant: { label: 'Grant', icon: '◆', badge: 'bg-emerald-500/20 text-emerald-300' },
    accelerator: { label: 'Accelerator', icon: '▲', badge: 'bg-purple-500/20 text-purple-300' },
    vc: { label: 'VC Contact', icon: '●', badge: 'bg-blue-500/20 text-blue-300' },
  };

  const config = typeConfig[card.type] || typeConfig.grant;

  const getDeadlineColor = (deadline?: string) => {
    if (!deadline) return '';
    // Check if it's a number (days)
    const days = parseInt(deadline);
    if (!isNaN(days)) {
      if (days <= 7) return 'text-red-400 bg-red-500/20';
      if (days <= 14) return 'text-amber-400 bg-amber-500/20';
      return 'text-slate-400 bg-slate-500/20';
    }
    // If it's text like "Under review", use default color
    return 'text-slate-400 bg-slate-500/20';
  };

  // Determine border color based on card type
  const borderColor = card.type === 'grant' ? 'emerald' : card.type === 'accelerator' ? 'purple' : 'blue';

  return (
    <div 
      className={`bg-gray-100 rounded-lg p-2.5 border-l-4 ${borderColors[borderColor]} shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => onCardClick?.(card.taskId)}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${config.badge}`}>
          {config.icon} {config.label}
        </span>
        {card.deadline && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getDeadlineColor(card.deadline)}`}>
            {card.deadline}
          </span>
        )}
      </div>
      <div className="text-sm font-semibold text-gray-800 mb-0.5">{card.title}</div>
      {(card.partner || card.firm) && (
        <div className="text-xs text-gray-500 mb-1">
          {card.partner}{card.firm && ` · ${card.firm}`}
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <div className="flex items-center gap-1">
          {card.amount && (
            <span className="text-emerald-600 font-semibold">{card.amount}</span>
          )}
          {card.status && (
            <span className="text-gray-400 ml-1">· {card.status}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700 font-medium">me</span>
          </div>
          <span className="text-gray-400">{card.taskId}</span>
        </div>
      </div>
    </div>
  );
}

export default function FinancesDashboard() {
  const [tasks] = useState<FinancesCard[]>(mockFinancesData);

  // Group tasks by stage
  const researchTasks = tasks.filter(t => t.stage === 0);
  const draftingTasks = tasks.filter(t => t.stage === 1);
  const submittedTasks = tasks.filter(t => t.stage === 2);
  const inReviewTasks = tasks.filter(t => t.stage === 3);
  const closedTasks = tasks.filter(t => t.stage === 4);

  // Calculate KPIs
  const totalPipeline = 425000; // $425k
  const activeApps = tasks.filter(t => t.stage < 4).length;
  const dueSoon = tasks.filter(t => {
    if (!t.deadline) return false;
    const days = parseInt(t.deadline);
    return !isNaN(days) && days <= 7;
  }).length;
  const secured = 75000; // $75k
  const winRate = 32; // 32%

  // Calculate column values
  const researchValue = '$95k potential';
  const draftingValue = '$180k potential';
  const submittedValue = '$125k potential';
  const inReviewValue = '$85k potential';
  const closedValue = '$75k secured';

  const handleCardClick = (taskId: string) => {
    // TODO: Handle card click (open modal, etc.)
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <StarsBackground />

      {/* Dashboard Header Bar with KPIs */}
      <div className="relative z-50 flex items-center flex-nowrap gap-6 mb-4 p-5 bg-slate-800/40 backdrop-blur-sm border-b border-slate-700/50 overflow-x-auto">
        {/* Dashboard Title */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-2xl">💰</span>
          <h1 className="text-xl font-semibold text-white">Finances Dashboard</h1>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-slate-600 flex-shrink-0"></div>

        {/* KPIs */}
        <div className="flex items-center gap-6 flex-shrink min-w-0 flex-nowrap">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">${(totalPipeline / 1000).toFixed(0)}k</div>
            <div className="text-xs text-slate-400">Total Pipeline</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{activeApps}</div>
            <div className="text-xs text-slate-400">Active Apps</div>
          </div>
          <div className="text-center">
            <div className="flex items-center gap-1 justify-center">
              <span className="text-2xl font-bold text-red-400">{dueSoon}</span>
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-xs text-slate-400">Due Soon</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">${(secured / 1000).toFixed(0)}k</div>
            <div className="text-xs text-slate-400">Secured</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{winRate}%</div>
            <div className="text-xs text-slate-400">Win Rate</div>
          </div>
        </div>

        {/* Filters - Right Side */}
        <div className="flex items-end gap-3 ml-auto flex-shrink-0">
          {/* Owner Filter */}
          <div>
            <label className="text-slate-500 text-xs block mb-1">Owner:</label>
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[140px]">
              <div className="w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center">
                <svg className="w-3 h-3 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-slate-300 text-sm flex-1">All Owners</span>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <label className="text-slate-500 text-xs block mb-1">Type:</label>
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[120px]">
              <span className="text-slate-300 text-sm flex-1">All Types</span>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Add Opportunity Button */}
          <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Opportunity
          </button>
        </div>
      </div>

      {/* Workflow view - Full Width */}
      <div className="relative z-10 flex-1 overflow-x-auto pb-4 px-4">
        <div className="flex gap-4 min-w-max">
          {([
            { title: 'Research', headerColor: 'slate' as const, stage: 0, cards: researchTasks },
            { title: 'Drafting', headerColor: 'blue' as const, stage: 1, cards: draftingTasks },
            { title: 'Submitted', headerColor: 'purple' as const, stage: 2, cards: submittedTasks },
            { title: 'In Review', headerColor: 'amber' as const, stage: 3, cards: inReviewTasks },
            { title: 'Closed', headerColor: 'emerald' as const, stage: 4, cards: closedTasks },
          ]).map(({ title, headerColor, stage, cards: columnCards }) => {
            const mappedCards: DashboardCardData[] = columnCards.map(card => ({
              id: card.id,
              title: card.title,
              type: card.type,
              taskId: card.taskId,
              assignedTo: card.assignedTo,
            }));
            return (
              <DashboardColumn
                key={stage}
                title={title}
                headerColor={headerColor}
                stage={stage}
                cards={mappedCards}
                count={columnCards.length}
                onCardClick={handleCardClick}
                renderCard={(dashCard) => {
                  const originalCard = columnCards.find(c => c.id === dashCard.id);
                  return originalCard ? <FinancesCardView card={originalCard} onCardClick={handleCardClick} /> : null;
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

